/**
 * @file: 问诊订单相关request
 */
import {
  ICommentInfo,
  IOrderAndDoctorInfo,
  IOrderInfo,
  ISupportOrder,
  IUnreadMsg,
  ICovid19OrderDetail,
  ORDER_STATUS,
  OrderState,
  RefundStatus,
  PayCompleteFlag,
  DOCTOR_ANSWER,
  ILastConsultRecord,
  IAssistantOrderListResult,
  IPrescriptOrderInfo,
  IConsultSummaryVo,
  TConsultSummaryAdd,
  TConsultSummaryUpdate,
  ISavePreConsultInfoReqBody,
  IPreConsultInfo
} from '@/schemas/order';
import { IDoctorInfo } from '@/schemas/doctor';
import { IConsultationOrderInfo } from '@/schemas/pay';
import { RESPONSE_SUCCESS_CODE } from '@/configs/constants';
import Toast from '@/components/Toast';
import serviceFormat from '@utils/services-format';
import { checkResponse, checkResponseCode } from '@/utils/helpers';
import { request } from './request';
import { ApiConfigs, Response } from '../schemas/service';

const api: ApiConfigs = {
  // 问诊订单列表
  queryOrderOfUser: {
    // url: '/health/api/v1/order/queryOrderByUserId', // 废弃
    url: '/health/api/v1/order/queryOrderByUserIdAndStatus',
    // url:
    //   'https://kapi.corp.kuaishou.com/mock/5058/health/api/v1/order/queryOrderByUserIdAndStatus',
    method: 'GET'
  },
  // 医生问诊记录
  queryOrderOfDoctor: {
    url: '/health/api/v1/doctorEnd/workbench/queryOrderByDoctorId',
    method: 'GET'
  },
  // 开方医生记录
  queryOrderOfAssistant: {
    url: 'health/api/v1/doctorEnd/workbench/queryOrderListByPrescriberId',
    method: 'GET'
  },
  deleteOrder: {
    url: '/health/api/v1/order/deleteOrderById',
    method: 'POST'
  },
  queryOrderDetail: {
    url: '/health/api/v1/order/queryOrderByOrderId',
    // url:
    //   'https://kapi.corp.kuaishou.com/mock/5058/health/api/v1/order/queryOrderByOrderId',
    method: 'GET'
  },
  uploadImage: {
    url: '/health/api/v1/consult/patientPicUpload',
    method: 'POST'
  },
  createOrder: {
    url: '/health/api/v1/order/createOrder',
    method: 'POST'
  },
  // 创建挂号订单
  createRegisterOrder: {
    url: '/health/api/v1/order/createOrderForRegister',
    method: 'POST'
  },
  // 取消挂号
  cancelRegistrationOrder: {
    url: '/health/api/v1/order/cancelRegistrationOrder',
    // url:
    //   'https://kapi.corp.kuaishou.com/mock/5058/health/api/v1/order/cancelRegistrationOrder', // test
    method: 'POST'
  },
  createComment: {
    url: '/health/api/v1/doctor/evaluate',
    method: 'POST'
  },
  getComment: {
    url: '/health/api/v1/doctor/getEvaluate',
    method: 'GET'
  },
  // 医生接诊
  acceptConsult: {
    url: '/health/api/v1/order/acceptConsult',
    method: 'POST'
  },
  // sendEndNotice: {
  //   url: '/health/api/v1/order/sendEndNotice',
  //   method: 'POST'
  // },
  supportOrder: {
    url: '/health/api/v1/order/queryOrderCondition',
    method: 'GET'
  },
  quickAcceptConsult: {
    url: '/health/api/v1/order/quickAcceptConsult',
    method: 'POST'
  },
  queryCovid19Order: {
    url: '/health/api/v1/nucleicAcid/order/getPageList',
    method: 'GET'
  },
  queryCovid19OrderDetail: {
    url: '/health/api/v1/nucleicAcid/order/getDetail',
    method: 'GET'
  },
  getCovid19BookingDetail: {
    url: '/health/api/v1/nucleicAcid/order/getReservationDetail',
    method: 'GET'
  },
  submitCovidBooking: {
    url: '/health/api/v1/nucleicAcid/order/submitAppoint',
    method: 'POST'
  },
  updateCovidBooking: {
    url: '/health/api/v1/nucleicAcid/order/updateAppoint',
    method: 'POST'
  },
  queryDoctorIncomeInfo: {
    url: '/health/api/v1/doctorEnd/workbench/queryDoctorIncomeInfo',
    method: 'GET'
  },
  queryDoctorEarnings: {
    url: '/health/api/v1/doctorEnd/workbench/queryDoctorEarnings',
    // url:
    //   'https://kapi.corp.kuaishou.com/mock/5058/health/api/v1/doctorEnd/workbench/queryDoctorEarnings', // 查询医生账户（待入账和总收入）
    method: 'GET'
  },
  completeOrder: {
    url: '/health/api/v1/order/completeOrder',
    method: 'POST'
  },
  // @see https://kapi.corp.kuaishou.com/project/5058/interface/api/517259
  queryDoctorTelReserve: {
    url: '/health/api/v1/doctor/queryDoctorTelReserve',
    method: 'GET'
  },
  deleteDoctorOrder: {
    url: '/health/api/v1/doctorEnd/workbench/deleteDoctorOrder',
    method: 'POST'
  },
  createOrderAdaptive: {
    url: '/health/api/v1/order/createOrderAdaptive', // 极速咨询
    method: 'POST'
  },
  getLastConsultRecord: {
    url: '/health/api/v1/order/latelyConsultingRecord', // 最近一次咨询记录
    method: 'GET'
  },
  queryOrderByPrescriptionId: {
    url: '/health/api/v1/doctorEnd/workbench/queryOrderByPrescriptionId',
    method: 'GET'
  },
  consultSummaryDetail: {
    url: '/health/api/v1/consult/consultSummaryDetail',
    method: 'GET'
  },
  consultSummary: {
    url: '/health/api/v1/consult/consultSummary',
    method: 'POST'
  },
  consultSummaryUpdate: {
    url: '/health/api/v1/consult/consultSummaryUpdate',
    method: 'POST'
  },
  savePreConsultInfo: {
    url: '/health/api/v1/consult/preSavePatient', // 预存咨询信息
    method: 'POST'
  },
  getRecentPreConsultInfo: {
    url: '/health/api/v1/consult/getRecentPatientInfo', // 查询最近一次预存咨询信息
    method: 'GET'
  },
  deletePopUpFlag: {
    url: '/health/api/v1/consult/deletePopUpFlag', // 删除弹框标识
    method: 'POST'
  },
  getConsultSummary: {
    url: '/health/api/v1/consult/getConsultSummary', // 获取订单的咨询小结信息
    method: 'GET'
  }
};

const orders = {
  // 获取用户问诊记录
  async queryOrderOfUser({ pageSize, pageNum, status }) {
    const params = {
      pageSize,
      pageNum,
      status
    };
    if (status === '0') {
      // 全部类型不需要传递
      delete params.status;
    }
    try {
      const response: Response = await request(api.queryOrderOfUser, params);
      // console.log('userOrderList:', response);
      const { result = [] } = response;
      const target: {
        list: IOrderAndDoctorInfo[];
        pageNum: number;
        pageSize: number;
        lastMark: false;
      } = {
        // list: result.list.filter((item) => !!item.doctorInfo),
        list: result.list
          .filter((order) => !!order.doctorInfo)
          .map((item) => {
            const { doctorInfo, orderInfo, prescriberOrderInfo } = item;
            const targetItem = {
              doctorInfo,
              orderInfo: {
                ...orderInfo,
                productAmount: orderInfo.productAmount
                  ? orderInfo.productAmount / 100
                  : 0, // 后端金额为[分]
                paymentAmount: orderInfo.paymentAmount
                  ? orderInfo.paymentAmount / 100
                  : 0, // 支付金额, 后端单位为[分]
                orderState: transOrderState(orderInfo)
              },
              prescriberOrderInfo
            };
            return targetItem;
          }),
        pageNum: result.pageNum,
        pageSize: result.pageSize,
        lastMark: result.lastMark
      };
      // console.log('---userOrderList', target.list);
      return target;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  // 获取医生问诊记录
  async queryOrderOfDoctor({ pageSize, pageNum, productType, status }) {
    try {
      const response: Response = await request(api.queryOrderOfDoctor, {
        pageSize,
        pageNum,
        productType,
        status
      });
      const { result = [] } = response;
      const target: {
        list: IOrderInfo[];
        pageNum: number;
        lastMark: boolean;
        pageSize: number;
      } = {
        list: result.list.map((item) => {
          const targetItem = {
            ...item,
            orderState: transOrderState(item)
          };
          return targetItem;
        }),
        pageNum: result.pageNum,
        lastMark: result.lastMark,
        pageSize: result.pageSize
      };
      return target;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async acceptConsult(orderId, departmentId?: number | string) {
    try {
      const response: Response<IOrderInfo> = await request(
        api.acceptConsult,
        undefined,
        {
          orderId,
          departmentId
        },
        { hideGlobalLoading: false }
      );
      const { result } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  // 咨询详情信息获取
  async getOrderDetail(orderId) {
    try {
      const response: Response = await request(api.queryOrderDetail, {
        orderId
      });
      // console.log('---getOrderDetail', response);

      const { code, result } = response;
      if (checkResponse(code, result)) {
        const target: {
          doctorInfo: IDoctorInfo | null;
          orderInfo: IOrderInfo | null;
        } = {
          doctorInfo: serviceFormat.addServiceTypeMapForDoctor(
            result.doctorInfo
          ),
          orderInfo: {
            ...result.orderInfo,
            productAmount:
              result.orderInfo && result.orderInfo.productAmount
                ? result.orderInfo.productAmount / 100
                : 0, // 后端金额为[分]
            paymentAmount:
              result.orderInfo && result.orderInfo.paymentAmount
                ? result.orderInfo.paymentAmount / 100
                : 0, // 后端金额为[分]
            couponAmount:
              result?.orderInfo && result?.orderInfo?.couponAmount
                ? result?.orderInfo?.couponAmount / 100
                : 0, // 后端金额为[分]
            orderState: transOrderState(result.orderInfo)
          }
        };
        return target;
      }
      return Promise.reject(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async deleteOrder(orderId) {
    try {
      const response: any = await request(
        api.deleteOrder,
        { orderId },
        undefined,
        {
          hideGlobalLoading: false
        }
      );
      if (response.code === 0) {
        return true;
      }
      return false;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async uploadImage(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response: Response = await request(api.uploadImage, {}, formData);
      const { result } = response;
      return result;
    } catch (error) {
      // TODO
      return Promise.reject(error);
    }
  },
  async createOrder(order) {
    try {
      const response: Response = await request(api.createOrder, {}, order, {
        hideToast: true
      });
      const { result } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async createOrderAdaptive(body: {
    doctorId: number;
    contactNum?: string;
    productType: number;
    relationId?: number;
    triageId: number;
    accessSource?: string;
    secondAccessSource?: string;
  }) {
    try {
      const response: Response<IOrderInfo> = await request(
        api.createOrderAdaptive,
        {},
        body,
        {
          hideToast: true,
          needResponse: true
        }
      );
      const { code, result, message } = response;
      return { code, result, message };
    } catch (error) {
      return Promise.reject(error);
    }
  },
  // 挂号订单创建
  async createRegisterOrder(order) {
    try {
      const response: Response = await request(
        api.createRegisterOrder,
        undefined,
        order,
        {
          needResponse: true
        }
      );
      const { code, message, result } = response;
      if (+code === RESPONSE_SUCCESS_CODE) {
        return result;
      }
      Toast.show(message);
      return false;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  // 取消挂号订单
  async cancelRegistrationOrder(payload) {
    const { hospitalIndexId, orderId } = payload;
    const postData = {
      hospitalIndexId,
      orderId
    };
    try {
      const response: any = await request(
        api.cancelRegistrationOrder,
        postData,
        undefined,
        {
          needResponse: true
        }
      );
      const { code, message, result } = response;
      if (+code === RESPONSE_SUCCESS_CODE) {
        // 第四次取消会有toast提示
        if (result && result.toast) {
          Toast.show(result.toast);
        } else {
          Toast.show('已取消预约');
        }
        return true;
      }
      Toast.show(message);
      return false;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async createComment(params) {
    try {
      const response: any = await request(api.createComment, undefined, params);
      if (response.code === 0) {
        return true;
      }
      return false;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async getCommentByOrder(orderId) {
    try {
      const response: any = await request(api.getComment, { orderId });
      const { result } = response;
      const target: ICommentInfo = {
        commentContent: result.commentContent || '',
        starLevel: result.starLevel || 0,
        tagContents: result.tagContents || []
      };
      return target;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  // async sendEndNotice(orderId) {
  //   try {
  //     const response: any = await request(api.sendEndNotice, { orderId });
  //     if (response.code === 0) {
  //       return true;
  //     }
  //     return false;
  //   } catch (error) {
  //     return Promise.reject(error);
  //   }
  // },
  async supportOrder(doctorId) {
    try {
      const response: Response<ISupportOrder> = await request(
        api.supportOrder,
        { doctorId }
      );
      const { result } = response;
      const { conditionValue = 0, message = 'ok', orderId } = result;
      return {
        conditionValue,
        message,
        orderId
      };
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async quickAcceptConsult(userId) {
    try {
      const response = await request(api.quickAcceptConsult, { userId });
      if (response.code === 0) {
        return true;
      }
      return false;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getCovid19Order(params) {
    const target = {
      pageNum: params.pageNum || 1,
      pageSize: params.pageSize || 10,
      status: params.status || 0
    };
    for (const i in target) {
      if (i === 'status' && target[i] === 0) {
        delete target[i];
      }
    }
    try {
      const response = await request(api.queryCovid19Order, target);
      const { result } = response;
      // console.log(result);
      if (response.code === 0) {
        return result;
      }
      return [];
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getCovid19OrderDetail({ id }) {
    try {
      const response: Response<ICovid19OrderDetail> = await request(
        api.queryCovid19OrderDetail,
        { id }
      );
      const { result } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async queryDoctorIncomeInfo({ pageNum = 1, pageSize = 10, status }) {
    try {
      const response = await request(api.queryDoctorIncomeInfo, {
        pageNum,
        pageSize,
        status
      });
      const { result } = response;
      return result as IConsultationOrderInfo;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async queryDoctorEarnings() {
    try {
      const response = await request(api.queryDoctorEarnings);
      const { result } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getCovid19BookingDetail(id) {
    try {
      const response: Response<any> = await request(
        api.getCovid19BookingDetail,
        { id }
      );
      const { result } = response;
      return result;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async submitCovidBooking({
    isUpdate,
    reservationUserInfoId,
    id,
    appointStartDate,
    appointEndDate
  }) {
    try {
      const response: Response<any> = await request(
        isUpdate ? api.updateCovidBooking : api.submitCovidBooking,
        {},
        { reservationUserInfoId, id, appointStartDate, appointEndDate },
        { needResponse: true }
      );
      const { code, message } = response;
      if (+code === RESPONSE_SUCCESS_CODE) {
        return true;
      }
      Toast.show(message);
      return false;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async completeOrder(
    orderId: number | string,
    departmentId?: number | string
  ) {
    try {
      const response = await request(api.completeOrder, {
        orderId,
        departmentId
      });
      if (response.code === 0) {
        return true;
      }
      return false;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async queryDoctorTelReserve(doctorId: number) {
    try {
      const response = await request(api.queryDoctorTelReserve, { doctorId });
      if (response.code === 0) {
        return Promise.resolve(response);
      }
      return Promise.reject(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async deleteDoctorOrder(orderId) {
    try {
      const response: any = await request(api.deleteDoctorOrder, { orderId });
      if (response.code === 0) {
        return Promise.resolve(true);
      }
      return Promise.resolve(false);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async getLastConsultRecord() {
    try {
      const response: Response<ILastConsultRecord | null> = await request(
        api.getLastConsultRecord
      );
      const { result } = response;
      if (result) {
        return result;
      }
      return Promise.reject();
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async queryOrderOfAssistant({ pageNum = 1, pageSize = 10, status }) {
    try {
      const response: Response<IAssistantOrderListResult> = await request(
        api.queryOrderOfAssistant,
        { pageNum, pageSize, status }
      );
      const { code, result } = response;
      if (checkResponse(code, result)) {
        return result;
      }
      return Promise.reject(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // 派单详情信息获取
  async getPrescriptOrderDetail({ prescriptionId }) {
    try {
      const response: Response = await request(api.queryOrderByPrescriptionId, {
        prescriptionId
      });
      const { code, result } = response;
      if (checkResponse(code, result)) {
        const target: {
          doctorInfo: IDoctorInfo | null;
          orderInfo: IOrderInfo | null;
          prescriberOrderInfo: IPrescriptOrderInfo | null;
        } = {
          doctorInfo: serviceFormat.addServiceTypeMapForDoctor(
            result.doctorInfo
          ),
          orderInfo: {
            ...result.orderInfo,
            productAmount:
              result.orderInfo && result.orderInfo.productAmount
                ? result.orderInfo.productAmount / 100
                : 0, // 后端金额为[分]
            paymentAmount:
              result.orderInfo && result.orderInfo.paymentAmount
                ? result.orderInfo.paymentAmount / 100
                : 0, // 后端金额为[分]
            orderState: transOrderState(result.orderInfo)
          },
          prescriberOrderInfo: result?.prescriberOrderInfo
        };
        return target;
      }
      return Promise.reject(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // 咨询小结详情
  async consultSummaryDetail(id: number) {
    try {
      const response: Response<IConsultSummaryVo> = await request(
        api.consultSummaryDetail,
        { id },
        undefined,
        { hideGlobalLoading: false }
      );
      const { code, result } = response;
      if (checkResponse(code, result)) {
        return result;
      }
      return Promise.reject(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async savePreConsultInfo(body: ISavePreConsultInfoReqBody) {
    try {
      const response: Response<IPreConsultInfo | null> = await request(
        api.savePreConsultInfo,
        undefined,
        { ...body },
        { hideGlobalLoading: false }
      );
      const { result } = response;
      if (result) {
        return result;
      }
      return Promise.reject();
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // 新增咨询小结
  async consultSummary(params: TConsultSummaryAdd) {
    try {
      const response: Response<TConsultSummaryAdd> = await request(
        api.consultSummary,
        undefined,
        params,
        { hideGlobalLoading: false }
      );
      const { code, result } = response;
      if (checkResponse(code, result)) {
        return result;
      }
      return Promise.reject(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async getRecentPreConsultInfo() {
    try {
      const response: Response<IPreConsultInfo | null> = await request(
        api.getRecentPreConsultInfo,
        undefined,
        undefined,
        { hideGlobalLoading: false }
      );
      const { result } = response;
      if (result) {
        return result;
      }
      return Promise.reject();
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // 咨询小结详情
  async consultSummaryUpdate(params: TConsultSummaryUpdate) {
    try {
      const response: Response<TConsultSummaryUpdate> = await request(
        api.consultSummaryUpdate,
        undefined,
        params
      );
      const { code, result } = response;
      if (checkResponse(code, result)) {
        return result;
      }
      return Promise.reject(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async deletePopUpFlag() {
    try {
      const response: Response = await request(
        api.deletePopUpFlag,
        undefined,
        undefined,
        { hideGlobalLoading: false, hideToast: true }
      );
      const { code } = response;
      if (checkResponseCode(code)) {
        return true;
      }
      return Promise.reject();
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async getConsultSummary(orderId) {
    try {
      const response: Response<IConsultSummaryVo | null> = await request(
        api.getConsultSummary,
        { orderId },
        undefined,
        { hideGlobalLoading: false, hideToast: true }
      );
      const { result } = response;
      if (result) {
        return result;
      }
      return Promise.reject();
    } catch (error) {
      return Promise.reject(error);
    }
  }
};

// 订单状态相关需求变更频繁, 此处将后端多个字段控制的订单状态组合为前端所需的一个字段:orderState 避免在业务组件或html写过多的变量判断, 业务组件尽量精简可提高代码可维护性
// 付费&免费订单各种状态转换, 详细逻辑参考 docs: https://docs.corp.kuaishou.com/d/home/fcAC6rYED-dPKsqJTHG28NEX6#
const transOrderState = (orderInfo: IOrderInfo) => {
  const {
    orderStatus,
    payComplete,
    refundStatus,
    checkRefund,
    paymentAmount,
    doctorAnswer
  } = orderInfo;
  // 付费、免费咨询被医生拒单
  if (
    orderStatus === ORDER_STATUS.HasConsulted &&
    doctorAnswer === DOCTOR_ANSWER.rejectAnswer
  ) {
    return OrderState.rejected;
  }
  // 付费订单
  if (paymentAmount) {
    switch (orderStatus) {
      case ORDER_STATUS.Initial:
        return OrderState.noPay;
      case ORDER_STATUS.HasPaid:
        return OrderState.hasPaid;
      case ORDER_STATUS.Consulting:
        return OrderState.hasDoctorReplay;
      case ORDER_STATUS.HasConsulted:
        if (payComplete === PayCompleteFlag.NotDone) {
          // 支付未完成, 已取消订单状态
          return OrderState.consultCanceled;
        }
        if (refundStatus === RefundStatus.NoApply) {
          // refundStatus 为 0
          // 未申请退款且不可申请退款, 不显示申请退款按钮; 未申请退款且可申请退款, 显示申请退款按钮
          return checkRefund
            ? OrderState.completeCanRefund
            : OrderState.completeNoRefund;
        }
        if (
          refundStatus === RefundStatus.Checking ||
          refundStatus === RefundStatus.Checked ||
          refundStatus === RefundStatus.Refunding ||
          refundStatus === RefundStatus.Fail
        ) {
          // refundStatus = 1,3,4,5, 则显示退款中状态
          if (doctorAnswer === DOCTOR_ANSWER.notAnswer) {
            return OrderState.doctorNoAcceptRefunding;
          }
          return OrderState.refunding;
        }
        if (refundStatus === RefundStatus.Success) {
          // refundStatus = 4 退款完成
          if (doctorAnswer === DOCTOR_ANSWER.notAnswer) {
            return OrderState.doctorNoAcceptRefunded;
          }
          return OrderState.refunded;
        }
        if (refundStatus === RefundStatus.Reject) {
          // refundStatus = 2 订单关闭, 退款被驳回状态
          return OrderState.refundCanceled;
        }
        // break;
        // 以上条件均不满足, 则返回已结束,不可申请退款的状态
        return OrderState.completeNoRefund;
      default:
        return OrderState.unKnow;
    }
  } else {
    // 免费订单
    switch (orderStatus) {
      case ORDER_STATUS.HasPaid:
        return OrderState.freeWaiting;
      case ORDER_STATUS.Consulting:
        return OrderState.freeConsulting;
      case ORDER_STATUS.HasConsulted:
        if (doctorAnswer === DOCTOR_ANSWER.notAnswer) {
          return OrderState.freeDoctorNoAccept;
        }
        return OrderState.freeCompleted;
      default:
        return OrderState.unKnow;
    }
  }
};
export default orders;
